import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { MediaUpload } from '@models/media.model';

import { environment } from '../../../environments/environment';

@Injectable()
export class MediaService {
  baseHref = `${environment.apiUrl}/media`;
  videoBaseHref = `${environment.apiUrl}/video`;

  // Allowed media file types for upload
  public allowedImageTypes = [
    'image/jpeg',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'image/apng',
    'image/avif',
    'image/svg+xml',
    'image/tiff',
    'image/bmp',
    'image/*',
  ];

  constructor(private http: HttpClient) {}

  uploadMediaForSupportTicket(
    mediaData: MediaUpload,
    files: any[],
  ): Observable<any> {
    const url = `${this.baseHref}/support-ticket`;
    const formData = new FormData();
    formData.append('data', JSON.stringify(mediaData));

    // Iterate through the files and append them to the FormData
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file && this.allowedImageTypes.includes(file.type)) {
        formData.append('files', file, file.name);
      }
    }
    return this.http.post<any>(url, formData);
  }

  uploadMedia(mediaData: MediaUpload, files: any[]): Observable<any> {
    const url = `${this.baseHref}/`;
    const formData = new FormData();
    formData.append('data', JSON.stringify(mediaData));

    // Iterate through the files and append them to the FormData
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file && this.allowedImageTypes.includes(file.type)) {
        formData.append('files', file, file.name);
      }
    }
    return this.http.post<any>(url, formData);
  }

  getVideoPresignedUrl(key: string, contentType: string): Observable<string> {
    const url = `${this.videoBaseHref}/presigned-url`;
    return this.http.get<string>(url, {
      params: {
        key,
        contentType,
      },
    });
  }

  uploadVideoWithPresignedUrl(
    presignedUrl: string,
    file: File,
  ): Observable<any> {
    return this.http.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      reportProgress: true,
      observe: 'events',
    });
  }

  createMedia(data: any): Observable<any> {
    const url = `${this.baseHref}/create`;
    return this.http.post<any>(url, data);
  }

  createMediaCollection(data: {
    resource: string;
    name: string;
  }): Observable<any> {
    const url = `${this.baseHref}/media-collection`;
    return this.http.post<any>(url, data);
  }

  deleteMediaById(data: { id: number; resource: string }): Observable<any> {
    const url = `${this.baseHref}/delete-media-by-id/${data.resource}/${data.id}`;
    return this.http.get<any>(url);
  }

  deleteMediaCollection(mediaCollectionId: number): Observable<any> {
    const url = `${this.baseHref}/media-collection/${mediaCollectionId}`;
    return this.http.delete<any>(url);
  }

  deleteMedia(data: {
    resource: string;
    resourceId: number;
    id?: number;
  }): Observable<any> {
    const url = `${this.baseHref}/delete-media`;
    return this.http.post<any>(url, data);
  }

  deleteMediaByUrl(data: { url: string; resource: string }): Observable<any> {
    const url = `${this.baseHref}/delete-media-by-url`;
    return this.http.post<any>(url, data);
  }

  fetchCollection(mediaCollectionId: number): Observable<any> {
    const url = `${this.baseHref}/media-collection/${mediaCollectionId}`;
    return this.http.get<any>(url);
  }

  fetchMedia(mediaData: {
    resource: string;
    resourceId: number;
  }): Observable<any> {
    const url = `${this.baseHref}/fetch-media`;
    return this.http.post<any>(url, mediaData);
  }

  updateMediaCollection(data: any): Observable<any> {
    const url = `${this.baseHref}/media-collection/${data.mediaCollectionId}`;
    return this.http.put<any>(url, data);
  }

  updateMediaCollectionName(data: {
    id: number;
    name: string;
  }): Observable<any> {
    const url = `${this.baseHref}/media-collection/name/${data.id}`;
    return this.http.put<any>(url, data);
  }
}
